<template>
  <div class="monitor-grid">
    <div
      v-for="(camera, index) in cameras"
      :key="camera.id"
      class="monitor-tile"
      :class="{ online: camera.url && !playbackErrors[camera.id] }"
    >
      <video
        v-if="camera.url"
        :src="camera.url"
        autoplay
        muted
        loop
        playsinline
        controls
        preload="metadata"
        @playing="clearError(camera.id)"
        @error="setError(camera.id)"
      ></video>

      <div v-else class="monitor-placeholder">
        <div class="camera-symbol">
          <icon name="video"></icon>
        </div>
        <span>信号待接入</span>
      </div>

      <div class="scan-line"></div>

      <div class="monitor-head">
        <div class="camera-name">
          <span class="camera-index">CAM {{ String(index + 1).padStart(2, '0') }}</span>
          <span>{{ camera.name }}</span>
        </div>
        <div class="camera-state" :class="getStateClass(camera)">
          <span class="state-dot"></span>
          {{ getStateText(camera) }}
        </div>
      </div>

      <div class="monitor-foot">
        <span>{{ camera.location }}</span>
        <span class="monitor-time">{{ currentTime }}</span>
        <button
          type="button"
          class="fullscreen-button"
          title="全屏查看"
          @click="openFullscreen($event.currentTarget.closest('.monitor-tile'))"
        >
          <icon name="expand"></icon>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { formatTime } from '@/utils/index.js'

export default {
  props: {
    cameras: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      currentTime: '',
      clockTimer: null,
      playbackErrors: {}
    }
  },
  mounted() {
    this.updateTime()
    this.clockTimer = setInterval(this.updateTime, 1000)
  },
  beforeDestroy() {
    clearInterval(this.clockTimer)
  },
  methods: {
    updateTime() {
      this.currentTime = formatTime(new Date(), 'HH:mm:ss')
    },
    setError(id) {
      this.$set(this.playbackErrors, id, true)
    },
    clearError(id) {
      this.$delete(this.playbackErrors, id)
    },
    getStateClass(camera) {
      if (!camera.url) return 'standby'
      return this.playbackErrors[camera.id] ? 'fault' : 'live'
    },
    getStateText(camera) {
      if (!camera.url) return '待接入'
      return this.playbackErrors[camera.id] ? '连接异常' : 'LIVE'
    },
    openFullscreen(element) {
      if (!element) return
      const requestFullscreen = element.requestFullscreen || element.webkitRequestFullscreen
      if (requestFullscreen) requestFullscreen.call(element)
    }
  }
}
</script>

<style lang="scss" scoped>
.monitor-grid {
  width: 100%;
  height: 438px;
  padding: 8px 12px 12px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-template-rows: repeat(2, minmax(0, 1fr));
  gap: 9px;
}

.monitor-tile {
  position: relative;
  min-width: 0;
  min-height: 0;
  overflow: hidden;
  border: 1px solid rgba(48, 167, 205, 0.3);
  border-radius: 4px;
  background-color: #07101e;
  box-shadow: inset 0 0 24px rgba(25, 137, 184, 0.09);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    pointer-events: none;
    background-image:
      linear-gradient(rgba(66, 178, 209, 0.035) 1px, transparent 1px),
      linear-gradient(90deg, rgba(66, 178, 209, 0.035) 1px, transparent 1px);
    background-size: 24px 24px;
  }

  &.online {
    border-color: rgba(53, 222, 193, 0.42);
    box-shadow: 0 0 13px rgba(28, 211, 190, 0.09);
  }

  video {
    width: 100%;
    height: 100%;
    display: block;
    object-fit: cover;
    background: #050b15;
  }
}

.monitor-placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: #61718d;
  font-size: 12px;

  .camera-symbol {
    width: 48px;
    height: 48px;
    display: grid;
    place-items: center;
    border: 1px solid rgba(65, 198, 221, 0.28);
    border-radius: 50%;
    color: #4c9eb8;
    font-size: 21px;
    box-shadow: 0 0 18px rgba(41, 180, 211, 0.1), inset 0 0 14px rgba(41, 180, 211, 0.08);
  }
}

.monitor-head,
.monitor-foot {
  position: absolute;
  z-index: 2;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  color: #dce8f7;
  background: rgba(5, 13, 27, 0.84);
  backdrop-filter: blur(2px);
}

.monitor-head {
  top: 0;
  height: 30px;
  padding: 0 9px;
  justify-content: space-between;
  border-bottom: 1px solid rgba(73, 180, 207, 0.16);
}

.camera-name {
  min-width: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;

  .camera-index {
    flex: 0 0 auto;
    color: #55d9e2;
    font-family: Consolas, monospace;
    font-size: 10px;
  }
}

.camera-state {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  gap: 5px;
  color: #8290a9;
  font-size: 10px;

  .state-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #71809a;
  }

  &.live {
    color: #5fe6c8;
    .state-dot {
      background: #42e4bd;
      box-shadow: 0 0 8px #42e4bd;
    }
  }

  &.fault {
    color: #ff7b6c;
    .state-dot {
      background: #ff6c5d;
      box-shadow: 0 0 8px rgba(255, 108, 93, 0.8);
    }
  }
}

.monitor-foot {
  bottom: 0;
  height: 27px;
  padding: 0 7px 0 9px;
  gap: 9px;
  color: #7787a3;
  font-size: 10px;
  border-top: 1px solid rgba(73, 180, 207, 0.14);
}

.monitor-time {
  margin-left: auto;
  color: #9babc3;
  font-family: Consolas, monospace;
}

.fullscreen-button {
  width: 22px;
  height: 22px;
  display: grid;
  place-items: center;
  border: 0;
  color: #73cbd8;
  background: transparent;
  cursor: pointer;
  font-size: 11px;

  &:hover {
    color: #ffffff;
    background: rgba(77, 205, 220, 0.12);
  }
}

.scan-line {
  position: absolute;
  z-index: 1;
  left: 0;
  right: 0;
  top: 30px;
  height: 1px;
  pointer-events: none;
  background: linear-gradient(90deg, transparent, rgba(59, 226, 217, 0.42), transparent);
  animation: scan 4.5s linear infinite;
}

@keyframes scan {
  from { transform: translateY(0); }
  to { transform: translateY(165px); }
}
</style>
